'use client';

import { useState, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import { createClient } from '@/lib/supabase/client';
import type { Employee, EmployeeTimeOffBalance } from '@/app/hr/components/types';

interface PtoRequestModalProps {
  open: boolean;
  employee: Employee;
  balances: EmployeeTimeOffBalance[];
  onClose: () => void;
  onSubmitted: () => void;
}

const BALANCE_LABELS: Record<string, { label: string; color: string; allowsSameDay: boolean }> = {
  pto:               { label: 'Paid Time Off',    color: '#E89A3C', allowsSameDay: false },
  sick:              { label: 'Sick Time',        color: '#dc2626', allowsSameDay: true  },
  bereavement:       { label: 'Bereavement',      color: '#6b7280', allowsSameDay: true  },
  jury_duty:         { label: 'Jury Duty',        color: '#7c3aed', allowsSameDay: false },
  parental:          { label: 'Parental Leave',   color: '#16a34a', allowsSameDay: false },
  floating_holiday:  { label: 'Floating Holiday', color: '#F5A623', allowsSameDay: false },
  unpaid:            { label: 'Unpaid Leave',     color: '#6b7280', allowsSameDay: false },
  other:             { label: 'Other',            color: '#6b7280', allowsSameDay: true  },
};

// Count business days (Mon-Fri) inclusive between two dates
function countBusinessDays(start: string, end: string): number {
  if (!start || !end) return 0;
  const s = new Date(start + 'T00:00:00');
  const e = new Date(end + 'T00:00:00');
  if (e < s) return 0;
  let count = 0;
  const cursor = new Date(s);
  while (cursor <= e) {
    const day = cursor.getDay(); // 0 = Sun, 6 = Sat
    if (day !== 0 && day !== 6) count += 1;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
}

const PtoRequestModal = ({ open, employee, balances, onClose, onSubmitted }: PtoRequestModalProps) => {
  const defaultBalance = balances.find(b => b.balance_type === 'pto') ?? balances[0];

  const [balanceType, setBalanceType] = useState<string>(defaultBalance?.balance_type ?? 'pto');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [hoursRequested, setHoursRequested] = useState<number>(8);
  const [hoursManuallyEdited, setHoursManuallyEdited] = useState(false);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const supabase = createClient();

  // Reset state when modal opens
  useEffect(() => {
    if (!open) return;
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = tomorrow.toISOString().slice(0, 10);
    setBalanceType(defaultBalance?.balance_type ?? 'pto');
    setStartDate(tomorrowStr);
    setEndDate(tomorrowStr);
    setHoursRequested(8);
    setHoursManuallyEdited(false);
    setNotes('');
    setSubmitting(false);
  }, [open, defaultBalance?.balance_type]);

  // Smart default hours: Mon-Fri × 8 when dates change, unless manually edited
  useEffect(() => {
    if (hoursManuallyEdited) return;
    const days = countBusinessDays(startDate, endDate);
    if (days > 0) setHoursRequested(days * 8);
  }, [startDate, endDate, hoursManuallyEdited]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [open, onClose]);

  const todayStr = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const tomorrowStr = useMemo(() => {
    const t = new Date();
    t.setDate(t.getDate() + 1);
    return t.toISOString().slice(0, 10);
  }, []);

  const balanceConfig = BALANCE_LABELS[balanceType] ?? BALANCE_LABELS.other;
  const allowsSameDay = balanceConfig.allowsSameDay;
  const minStartDate = allowsSameDay ? todayStr : tomorrowStr;

  const currentBalance = balances.find(b => b.balance_type === balanceType);
  const remainingHours = currentBalance?.hours_remaining ?? 0;
  const wouldExceedBalance = hoursRequested > remainingHours;

  // Validation
  const dateValid = !!startDate && !!endDate && new Date(endDate) >= new Date(startDate);
  const startDateValid = !!startDate && (allowsSameDay ? startDate >= todayStr : startDate >= tomorrowStr);
  const hoursValid = hoursRequested > 0;
  const isValid = dateValid && startDateValid && hoursValid && !!balanceType && !submitting;

  const handleSubmit = async () => {
    if (!isValid) return;

    if (wouldExceedBalance) {
      const confirmMsg = `You are requesting ${hoursRequested}h of ${balanceConfig.label} but only have ${remainingHours.toFixed(1)}h remaining. Your manager will see this exceeds your balance. Submit anyway?`;
      if (!confirm(confirmMsg)) return;
    }

    setSubmitting(true);
    const payload: Record<string, unknown> = {
      employee_id: employee.id,
      balance_type: balanceType,
      start_date: startDate,
      end_date: endDate,
      hours_requested: hoursRequested,
      notes: notes.trim() || null,
    };

    console.log('[PtoRequest] Submitting:', payload);

    for (let attempt = 0; attempt < 3; attempt++) {
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error(`Submit timed out (attempt ${attempt + 1}/3)`)), 8000));
      const opPromise = supabase.from('employee_time_off_requests').insert(payload)
        .then((res) => { if (res.error) throw res.error; return res.data; });

      try {
        await Promise.race([opPromise, timeoutPromise]);
        console.log(`[PtoRequest] Submitted attempt ${attempt + 1}`);
        toast.success(
          `Request submitted — ${hoursRequested}h ${balanceConfig.label}. Awaiting manager approval.`,
          { duration: 4000, icon: '🌴' }
        );
        onSubmitted();
        setSubmitting(false);
        onClose();
        return;
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        const isRateLimit = msg.includes('429') || msg.toLowerCase().includes('too many');
        const isTimeout = msg.includes('timed out');
        console.warn(`[PtoRequest] Attempt ${attempt + 1} failed: ${msg}`);
        if (attempt === 2 || (!isRateLimit && !isTimeout)) {
          toast.error(`Submit failed: ${msg}`, { duration: 6000 });
          setSubmitting(false);
          return;
        }
        await new Promise(r => setTimeout(r, 500 * Math.pow(2, attempt)));
      }
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[120] flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="pto-request-title"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-5 border-b border-gray-100">
          <div>
            <h3 id="pto-request-title" className="text-lg font-bold text-[#1C2E5E]">Request Time Off</h3>
            <p className="text-[12px] text-gray-500 mt-0.5">Your manager will be notified to approve.</p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Balance type pills */}
          <div>
            <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">Balance Type</label>
            <div className="grid grid-cols-2 gap-2">
              {balances.map(b => {
                const cfg = BALANCE_LABELS[b.balance_type] ?? BALANCE_LABELS.other;
                const active = balanceType === b.balance_type;
                return (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => setBalanceType(b.balance_type)}
                    className={`px-3 py-2 rounded-lg text-[12px] font-semibold transition-all text-left ${active ? 'text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                    style={active ? { background: cfg.color } : undefined}
                  >
                    <div>{cfg.label}</div>
                    <div className={`text-[10px] mt-0.5 ${active ? 'opacity-90' : 'text-gray-400'}`}>
                      {b.hours_remaining.toFixed(1)}h available
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Same-day eligibility note */}
          {!allowsSameDay && (
            <p className="text-[10px] text-amber-700 bg-amber-50 rounded-lg px-3 py-2 border border-amber-100">
              ℹ {balanceConfig.label} requests must start tomorrow or later. For same-day requests, choose Sick Time.
            </p>
          )}

          {/* Date range */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">
                Start Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={startDate}
                min={minStartDate}
                onChange={e => {
                  setStartDate(e.target.value);
                  if (endDate && new Date(endDate) < new Date(e.target.value)) {
                    setEndDate(e.target.value);
                  }
                }}
                className="w-full px-3 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none transition-all"
              />
              {startDate && startDate < minStartDate && (
                <p className="text-[10px] text-red-600 mt-1">
                  Start date must be {allowsSameDay ? 'today or later' : 'tomorrow or later'}
                </p>
              )}
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">
                End Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={endDate}
                min={startDate || minStartDate}
                onChange={e => setEndDate(e.target.value)}
                className="w-full px-3 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none transition-all"
              />
              {endDate && startDate && new Date(endDate) < new Date(startDate) && (
                <p className="text-[10px] text-red-600 mt-1">End date must be on or after start date</p>
              )}
            </div>
          </div>

          {/* Hours */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[11px] font-semibold text-gray-700">
                Hours Requested <span className="text-red-500">*</span>
              </label>
              {!hoursManuallyEdited && countBusinessDays(startDate, endDate) > 0 && (
                <span className="text-[10px] text-gray-400">
                  Auto-calculated from {countBusinessDays(startDate, endDate)} business day{countBusinessDays(startDate, endDate) !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            <input
              type="number"
              min="0.5"
              step="0.5"
              value={hoursRequested}
              onChange={e => { setHoursRequested(Number(e.target.value)); setHoursManuallyEdited(true); }}
              className="w-full px-3 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none transition-all"
            />
            <div className="flex items-center justify-between mt-1.5">
              <p className={`text-[11px] ${wouldExceedBalance ? 'text-amber-700 font-bold' : 'text-gray-500'}`}>
                {wouldExceedBalance
                  ? `⚠ Exceeds available balance by ${(hoursRequested - remainingHours).toFixed(1)}h`
                  : `${(remainingHours - hoursRequested).toFixed(1)}h would remain after this request`}
              </p>
              {hoursManuallyEdited && (
                <button
                  type="button"
                  onClick={() => {
                    setHoursManuallyEdited(false);
                    const days = countBusinessDays(startDate, endDate);
                    if (days > 0) setHoursRequested(days * 8);
                  }}
                  className="text-[10px] text-[#E89A3C] hover:underline"
                >
                  Reset to auto
                </button>
              )}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">
              Notes <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder={balanceType === 'sick' ? 'Optional — feel better soon!' : 'Reason or destination, e.g. "Vacation to Mexico"'}
              className="w-full px-3 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none transition-all resize-none"
            />
            <p className="text-[10px] text-gray-400 mt-1">{notes.length}/500</p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 p-5 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-[13px] font-semibold text-gray-600 hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!isValid}
            className="px-4 py-2 rounded-lg text-[13px] font-semibold text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: balanceConfig.color }}
          >
            {submitting ? 'Submitting…' : 'Submit Request'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PtoRequestModal;
